import React, {useContext, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";

import FormControl from "../../../components/form/FormControl.jsx";
import FormContainer from "../../../components/form/FormContainer.jsx";

import {ProjectApi} from "../../../api/authority/generated/index.ts";
import {useErrorTranslation} from "../../../api/utilities/translateErrorMessage.js";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";
import useDownloadState from "../../../api/utilities/useDownloadState.js";
import Button from "../../../components/Button.jsx";

export default function BillingDataForm({project, onSave}) {
    const {t} = useTranslation();
    const {et} = useErrorTranslation();
    const {context, getAuthorityConfiguration} = useContext(GlobalContext);

    const fieldDefinitions = [
        {name: "email", required: true, translation: t("billing.form.email")},
        {name: "countryCode", required: true, translation: t("billing.form.country")},
        {name: "companyName", required: true, translation: t("billing.form.companyName")},
        {name: "street", translation: t("billing.form.street")},
        {name: "city", required: true, translation: t("billing.form.city")},
        {name: "zip", required: true, translation: t("billing.form.zip")},
        {name: "companyRegistrationNumber", translation: t("billing.form.companyRegistrationNumber")},
        {name: "vatNumber", translation: t("billing.form.vatNumber")},
    ];

    let initialState = useMemo(() => {
        const initialState = fieldDefinitions.reduce((acc, field) => {
            acc[field.name] = project.billingAccount?.[field.name] || "";
            return acc;
        }, {});
        initialState.countryCode = project.billingAccount?.countryCode || "CZ";

        // Set email default value
        initialState.email = project.billingAccount?.email || context.currentIdentity?.user?.email || "";

        return initialState;
    }, [context.currentIdentity?.user?.email, project.billingAccount, project.id]);

    // Initialize billing account input state
    const [billingAccountInput, setBillingAccountInput] = useState(initialState);

    useEffect(() => {
        setBillingAccountInput(initialState);
    }, [initialState]);

    const [saveState, setDownloading, setResult] = useDownloadState();
    const [formError, setFormError] = useState(null);

    // Handle saving billing data
    const handleSaveBillingData = async () => {
        const requiredFields = fieldDefinitions.filter((field) => field.required).map((field) => field.name);
        if (!requiredFields.every((field) => billingAccountInput[field]?.trim())) {
            setFormError(t("form.fill_required_fields"));
            return;
        }

        const normalizedInput = {
            ...billingAccountInput,
            zip: billingAccountInput.zip.replace(/\s+/g, '').replace(/^(\d{3})(\d{2})$/, '$1 $2'),
            companyRegistrationNumber: billingAccountInput.companyRegistrationNumber.replace(/\s+/g, ''),
            vatNumber: billingAccountInput.vatNumber.replace(/\s+/g, '').toUpperCase(),
        };
        if (!/^\d{3} \d{2}$/.test(normalizedInput.zip)) {
            setFormError(t('billing.form.zip-error'));
            return;
        }
        const expectedVatPrefix = normalizedInput.countryCode;
        if (normalizedInput.companyRegistrationNumber && !/^\d{8}$/.test(normalizedInput.companyRegistrationNumber)) {
            setFormError(t('billing.form.companyRegistrationNumber-error'));
            return;
        }
        if (/^\d+$/.test(normalizedInput.vatNumber)) {
            normalizedInput.vatNumber = expectedVatPrefix + normalizedInput.vatNumber;
        }
        const vatPattern = expectedVatPrefix === 'SK' ? /^SK\d{10}$/ : /^CZ\d{8,10}$/;
        if (normalizedInput.vatNumber && !vatPattern.test(normalizedInput.vatNumber)) {
            setFormError(t('billing.form.vatNumber-error', {defaultValue: 'Zadejte celé DIČ včetně CZ nebo SK, například CZ21414319.'}));
            return;
        }
        if (normalizedInput.vatNumber && !normalizedInput.vatNumber.startsWith(expectedVatPrefix)) {
            setFormError(t('billing.form.vatNumber-country-error', {prefix: expectedVatPrefix}));
            return;
        }

        setFormError(null);
        setBillingAccountInput(normalizedInput);
        setDownloading(true);
        try {
            const response = await new ProjectApi(getAuthorityConfiguration()).changeBillingAccount({
                projectId: project.id,
                billingAccountInput: normalizedInput,
            });

            setResult(response);
            toast.success(t("saved"));

            if (typeof onSave === "function") {
                onSave(response);
            }
        } catch (e) {
            const errorMessage = await et(e);
            setFormError(errorMessage);
        } finally {
            setDownloading(false);
        }
    };

    // Handle field changes
    const handleFieldChange = (fieldName, value) => {
        setFormError(null);
        setBillingAccountInput((prevState) => ({
            ...prevState,
            [fieldName]: value,
        }));
    };

    return (
        <FormContainer>
            {formError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200" role="alert">
                    {formError}
                </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
                {fieldDefinitions.map((field) => (
                    <div
                        key={field.name}
                        className={['email', 'companyName', 'street'].includes(field.name) ? 'sm:col-span-2' : ''}
                    >
                    {field.name === 'countryCode' ? (
                        <label className="block">
                            <span className="mb-1.5 block text-sm text-slate-700 dark:text-slate-300">{field.translation}*</span>
                            <select
                                name="countryCode"
                                value={billingAccountInput.countryCode}
                                required
                                onChange={(e) => handleFieldChange('countryCode', e.target.value)}
                                className="form-select w-full rounded-lg border-slate-300 dark:border-slate-600 dark:bg-slate-800"
                            >
                                <option value="CZ">{t('billing.form.country-cz')}</option>
                                <option value="SK">{t('billing.form.country-sk')}</option>
                            </select>
                        </label>
                    ) : (
                    <FormControl
                        type={field.type || "text"}
                        label={`${field.translation}${field.required ? "*" : ""}`}
                        name={field.name}
                        variant="modern"
                        value={billingAccountInput[field.name]}
                        required={field.required || false}
                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    />
                    )}
                        {field.name === 'email' && <p className="mt-1.5 text-xs leading-5 text-slate-500 dark:text-slate-400">
                            {t("billing.form.email-caption")}
                        </p>}
                    </div>
                ))}
            </div>

            {/* Save Button */}
            <div className="mt-5 flex justify-end">
                <Button
                    size="normal"
                    onClick={handleSaveBillingData}
                    disabled={saveState.downloading}
                >
                    {t('save')}
                </Button>
            </div>
        </FormContainer>
    );
}
